// Deux lectures DISTINCTES, sur deux tables distinctes :
//   - la DÉPENSE (`ai_spend`, grand livre append-only, migration
//     20260729120000) → `fetchRunCostsForCapture`, le log de coût de la fiche ;
//   - l'ÉTAT (`ai_runs`, clé d'idempotence mutée en place) →
//     `fetchIngestRunStatus`, qui alimente le verrou de ré-analyse.
// Ne pas confondre : `ai_runs` écrase le coût à chaque re-run (c'est un verrou,
// pas un journal), `ai_spend` ne perd rien.
//
// RLS : `ai_spend_read` est calquée sur `ai_runs_read` durcie en phase C
// (migration 20260722120000_budget_guard.sql) — admin (is_admin()) OU capture
// visible par l'appelant (RLS `captures` appliquée au sous-select). Les
// fonctions de ce module filtrent déjà par `capture_id` : un non-admin voit le
// log de coût de SES captures, à l'identique.
//
// Lignes triées par occurred_at (chronologie réelle). On NE déduplique PAS
// les retries (un même kind apparaît plusieurs fois si relance) — c'est
// précisément l'info utile dans un log de coût, et depuis `ai_spend` la base
// sait enfin la livrer.

import type { SupabaseClient } from "@supabase/supabase-js";

export type RunStage = "stt" | "extract" | "audit" | "other";

export interface RunCostRow {
  stage: RunStage;
  model: string;
  costCents: number | null;
  createdAt: string;
}

interface RunDbRow {
  kind: string;
  model: string | null;
  cost_cents: number | string | null;
  occurred_at: string;
}

// kind='transcribe' couvre deux étapes distinctes côté code : STT (Replicate)
// et extract LLM (OpenAI/Gemini/Anthropic). On les distingue par le prefix
// vendor du `model` — c'est le cas des lignes ANTÉRIEURES à la phase A, depuis
// laquelle le coût Replicate porte son propre kind='stt'. `audit` reste tel
// quel ; autres kinds (stt_identify, embed, cluster, summarize) tombent dans
// "other" — rare côté capture mais on les affiche.
function classify(kind: string, model: string): RunStage {
  if (kind === "audit") return "audit";
  if (kind === "stt") return "stt";
  if (kind === "transcribe") {
    if (model.startsWith("replicate:")) return "stt";
    return "extract";
  }
  return "other";
}

function parseCostCents(value: number | string | null): number | null {
  if (value === null || value === undefined) return null;
  const n = typeof value === "string" ? Number(value) : value;
  if (!Number.isFinite(n)) return null;
  return n;
}

export type FetchRunCostsResult = { ok: true; data: RunCostRow[] } | { ok: false; error: string };

export async function fetchRunCostsForCapture(
  client: SupabaseClient,
  captureId: string,
): Promise<FetchRunCostsResult> {
  const { data, error } = await client
    .from("ai_spend")
    .select("kind, model, cost_cents, occurred_at")
    .eq("capture_id", captureId)
    .order("occurred_at", { ascending: true });
  if (error) return { ok: false, error: error.message };

  const rows = (data ?? []) as RunDbRow[];
  const out: RunCostRow[] = rows.map((r) => {
    const model = r.model ?? "—";
    return {
      stage: classify(r.kind, model),
      model,
      costCents: parseCostCents(r.cost_cents),
      // `createdAt` conservé côté UI (RunCostsLog s'en sert comme clé de liste) —
      // c'est le `occurred_at` du grand livre.
      createdAt: r.occurred_at,
    };
  });
  return { ok: true, data: out };
}

// État de l'extraction d'items lu depuis ai_runs (Partie B3/B4). L'extraction
// est encodée par kind='transcribe' + prompt_version commençant par 'ingest'
// (convention historique : pas de valeur d'enum `ingest` dédiée). Un row
// status='failed' sur cette clé = échec d'extraction visible (grâce à B1).
//
// NOTE : la run STT (Replicate) porte aussi kind='transcribe' mais un
// prompt_version distinct (`stt-replicate-v1`) — on filtre donc sur le préfixe
// `ingest` pour ne pas confondre un échec STT avec un échec d'extraction.
interface IngestRunRow {
  prompt_version: string;
  status: string;
  attempts: number | null;
  created_at?: string | null;
}

// Run ingest la plus récente — `startedAt` = départ du run (posé par le verrou
// claimIngestRun côté edge, préservé par l'écriture de succès). Sert au panneau
// Sujets pour détecter une ré-analyse en vol (cf. reanalysis.ts).
export interface LatestIngestRun {
  status: string;
  startedAt: string;
}

export type IngestRunStatusResult =
  | {
      ok: true;
      failed: boolean;
      succeeded: boolean;
      attempts: number;
      latestRun: LatestIngestRun | null;
    }
  | { ok: false; error: string };

export async function fetchIngestRunStatus(
  client: SupabaseClient,
  captureId: string,
): Promise<IngestRunStatusResult> {
  const { data, error } = await client
    .from("ai_runs")
    .select("prompt_version, status, attempts, created_at")
    .eq("capture_id", captureId)
    .eq("kind", "transcribe");
  if (error) return { ok: false, error: error.message };

  const rows = (data ?? []) as IngestRunRow[];
  const ingestRuns = rows.filter((r) => (r.prompt_version ?? "").startsWith("ingest"));
  // Échec si AUCUN ingest n'a réussi ET au moins un a échoué (un succès ultérieur
  // efface l'échec : le re-run idempotent réécrit la même clé en success).
  const succeeded = ingestRuns.some((r) => r.status === "success");
  const anyFailed = ingestRuns.some((r) => r.status === "failed");
  // Max des compteurs de tentatives (garde anti-boucle : « file d'attente » tant
  // qu'on est sous le plafond, « échec » une fois épuisé). Aligné côté serveur.
  const attempts = ingestRuns.reduce((max, r) => Math.max(max, r.attempts ?? 0), 0);
  // Run la plus récente parmi les rows ingest datées (plusieurs prompt_version
  // peuvent coexister après un bump) — les rows sans created_at parseable sont
  // ignorées plutôt que de fabriquer un « départ » faux.
  let latestRun: LatestIngestRun | null = null;
  let latestMs = Number.NEGATIVE_INFINITY;
  for (const r of ingestRuns) {
    const ms = r.created_at ? new Date(r.created_at).getTime() : Number.NaN;
    if (!Number.isNaN(ms) && ms > latestMs) {
      latestMs = ms;
      latestRun = { status: r.status, startedAt: r.created_at as string };
    }
  }
  return { ok: true, failed: anyFailed && !succeeded, succeeded, attempts, latestRun };
}
